import { ColDef } from 'ag-grid-enterprise';
import { alignLeft, alignRight, component } from './utils';
import { h } from 'vue';
import { VIcon, VTooltip } from 'vuetify/lib';
import { agreementPopoverAdapter } from './common';
import { OmsBasketItem } from '@/modules/marketplace/models';
import { Security } from '@/modules/common/models';
import Decimal from 'decimal.js';
import { RateType } from '@/connect';
import { OrderIntent, OrderType, TimeInForceType } from '@/modules/marketplace/types/marketplace';
import * as cols from './common';
import {
  orderIntentLabel,
  orderTypeLabel,
  timeInForceAbbr,
} from '@/modules/marketplace/helpers/marketplace';
import { useAgreementsStore } from '@/store/agreements-store';
export { errorRow, errorField, errorMessage } from './common';

export function security(): ColDef<OmsBasketItem, Security> {
  return {
    field: 'security',
    colId: 'security',
    headerName: 'Security',
    sortable: false,
    valueFormatter: (params) =>
      !params.value ? '–' : `${params.value.ticker} [${params.value.cusip}]`,
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function quantity(): ColDef<OmsBasketItem, number> {
  return cols.quantity({
    field: 'quantity',
    colId: 'quantity',
    headerName: 'Quantity',
    dashWhenZero: true,
  });
}

export function rate(): ColDef<OmsBasketItem, Decimal | null> {
  return {
    field: 'rate',
    colId: 'rate',
    headerName: 'Rebate Rate',
    sortable: false,
    cellRendererSelector: (params) =>
      cols.rateAdapter({
        rate: params.value,
        rateModifier: undefined,
      }),
    cellDataType: 'text',
    ...alignRight({ hasPostfix: true }),
  };
}

export function rateType(): ColDef<OmsBasketItem, RateType> {
  return {
    field: 'rateType',
    colId: 'rateType',
    headerName: 'Rate Type',
    sortable: false,
    valueFormatter: (params) => (params.value === RateType.REBATE ? 'Rebate Rate' : 'Fee'),
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function active(): ColDef<OmsBasketItem, boolean> {
  return {
    field: 'active',
    colId: 'active',
    headerName: 'Active',
    sortable: false,
    valueFormatter: (params) => (params.value ? 'Yes' : 'No'),
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function minQuantity(): ColDef<OmsBasketItem, number> {
  return cols.quantity({
    field: 'minQuantity',
    colId: 'minQuantity',
    headerName: 'Min Qty.',
    dashWhenZero: true,
  });
}

export function orderType(): ColDef<OmsBasketItem, OrderType> {
  return {
    field: 'type',
    colId: 'type',
    headerName: 'Order Type',
    sortable: false,
    valueFormatter: (params) => orderTypeLabel(params.value!),
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function orderIntent(): ColDef<OmsBasketItem, OrderIntent> {
  return {
    field: 'intent',
    colId: 'intent',
    headerName: 'Order Intent',
    sortable: false,
    valueFormatter: (params) => orderIntentLabel(params.value!),
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function timeInForceType(): ColDef<OmsBasketItem, TimeInForceType> {
  return {
    field: 'timeInForceType',
    colId: 'timeInForceType',
    headerName: 'Time In Force',
    sortable: false,
    valueFormatter: (params) => timeInForceAbbr(params.value!),
    cellDataType: 'text',
    ...alignLeft(),
  };
}

export function agreements(): ColDef<OmsBasketItem, string[]> {
  const agreementsStore = useAgreementsStore();

  return {
    field: 'agreementIds',
    colId: 'agreements',
    headerName: 'Agreement',
    sortable: false,
    valueFormatter: (params) =>
      params.value
        ?.map((agreementId) => agreementsStore.getAgreement(agreementId)?.shortName)
        .join(', ') || 'All',
    cellRendererSelector: (params) =>
      agreementPopoverAdapter({
        agreementIds: params.value ?? [],
      }),
    ...alignLeft(),
  };
}

const exceedsSoftLimitMessageAdaptor = component<{
  item: OmsBasketItem;
  softLimitValue: Decimal | null;
  entryExceedsSoftLimit(entry: OmsBasketItem): boolean;
}>(
  (props) => () =>
    h(VTooltip, {
      props: { color: 'form-tooltip', 'max-width': '600px', right: true },
      scopedSlots: {
        activator: ({ on, attrs }) =>
          h(
            'span',
            { props: attrs, on },
            props.entryExceedsSoftLimit(props.item)
              ? [h(VIcon, { props: { color: 'yellow darken-2' } }, ['mdi-alert-circle'])]
              : []
          ),
        default: () =>
          h(
            'span',
            `The notional value of your order exceeds your soft limit of $${props.softLimitValue}`
          ),
      },
    })
);

export function exceedsSoftLimitMessage(config: {
  softLimitValue: Decimal | null;
  entryExceedsSoftLimit: (entry: OmsBasketItem) => boolean;
}): ColDef<OmsBasketItem> {
  return {
    colId: 'exceedsSoftLimitMessage',
    headerName: '',
    sortable: false,
    cellDataType: 'text',
    cellRendererSelector: (params) =>
      exceedsSoftLimitMessageAdaptor({
        item: params.data!,
        softLimitValue: config.softLimitValue,
        entryExceedsSoftLimit: config.entryExceedsSoftLimit,
      }),
    ...alignLeft(),
  };
}
